/**
 * usePushNotifications — registers the service worker and exposes
 * subscribe/unsubscribe helpers so the AI (via a choreography tool
 * `request_push_subscription` broadcast to the open tab) can prompt
 * the user for notification permission and persist the subscription
 * to the server.
 *
 * Both an explicit browser alert control and AI choreography can use
 * requestSubscribe. Permission is requested only after one of those
 * explicit actions, never automatically when a page loads.
 *
 * Lifecycle:
 *   1. On mount, register /sw.js (if supported + not already registered)
 *   2. Listen for `choreography` events with tool="request_push_subscription"
 *      from BrowserBus
 *   3. On request, call Notification.requestPermission()
 *   4. If granted, pushManager.subscribe() → POST to /api/v1/push/subscribe
 *   5. Surface success/error back to the AI via render_diff_panel or
 *      a chat message (handled by the AI from MCP tool response)
 *
 * Sub-status (one of):
 *   "unsupported" — browser is missing a required notification/push API
 *   "registration_failed" — APIs exist but the worker could not register
 *   "registration_unconfirmed" — local subscription exists but server save is not confirmed
 *   "denied"      — user blocked notifications system-wide
 *   "default"     — never asked; can prompt
 *   "subscribed"  — browser and server registration are confirmed
 *   "subscription_revoked" — subscribed once but pushManager returned null
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { getCsrfHeader } from "../lib/csrf";

export type PushStatus =
  | "unsupported"
  | "registration_failed"
  | "registration_unconfirmed"
  | "denied"
  | "default"
  | "subscribed"
  | "subscription_revoked"
  | "checking";

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(b64);
  // Explicit ArrayBuffer so the result satisfies BufferSource (not SharedArrayBuffer).
  const out = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

function isSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

async function getServerVapidKey(): Promise<string | null> {
  try {
    const res = await fetch("/api/v1/push/config", { credentials: "include", signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const j = await res.json();
    return j.enabled && j.public_key ? (j.public_key as string) : null;
  } catch {
    return null;
  }
}

async function readyWorker(): Promise<ServiceWorkerRegistration> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([navigator.serviceWorker.ready, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error("Service worker is not ready. Refresh this page and try again.")), 8000);
    })]);
  } finally { clearTimeout(timer); }
}

async function serverRegistrationConfirmed(endpoint: string): Promise<boolean> {
  try {
    const response = await fetch("/api/v1/push/registration", {
      method: "POST", credentials: "include", signal: AbortSignal.timeout(8000),
      headers: {"content-type": "application/json", ...getCsrfHeader()},
      body: JSON.stringify({endpoint}),
    });
    return response.ok && (await response.json())?.registered === true;
  } catch { return false; }
}

function registrationSaveFailure(status: number): string {
  if (status === 401) return "Sign in again, then enable this browser.";
  if (status === 403) return "Refresh this page, then enable this browser again.";
  if (status === 400) return "This browser's push registration could not be accepted. Refresh and try enabling it again.";
  return "This browser's registration could not be saved. Try enabling it again later.";
}

export interface BrowserPushTestResult {
  ok: boolean; reason?: string; provider_accepted?: boolean; device_display_confirmed?: false;
  in_app_receipt_id?: string | null; message?: string;
}

export function usePushNotifications(listenForRequests = true): {
  status: PushStatus;
  serverEnabled: boolean | null;
  requestTest: (workspaceId?: string) => Promise<BrowserPushTestResult>;
  requestSubscribe: () => Promise<{ ok: boolean; reason?: string }>;
  unsubscribe: () => Promise<{ ok: boolean; reason?: string }>;
} {
  const [status, setStatus] = useState<PushStatus>("checking");
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null);
  const serverPublicKey = useRef<string | null>(null);

  // Register the service worker once on mount + compute initial status.
  useEffect(() => {
    if (!isSupported()) {
      setStatus("unsupported");
      return;
    }
    let cancelled = false;

    (async () => {
      const publicKey = await getServerVapidKey();
      if (!cancelled) { serverPublicKey.current = publicKey; setServerEnabled(Boolean(publicKey)); }
      try {
        await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      } catch {
        // Registration failure — push won't work. Don't crash the page.
        if (!cancelled) setStatus("registration_failed");
        return;
      }
      const perm = Notification.permission;
      if (perm === "denied") {
        if (!cancelled) setStatus("denied");
        return;
      }
      if (perm === "default") {
        if (!cancelled) setStatus("default");
        return;
      }
      // granted — check whether we already have an active subscription
      try {
        const reg = await readyWorker();
        const sub = await reg.pushManager.getSubscription();
        const saved = sub ? await serverRegistrationConfirmed(sub.endpoint) : false;
        if (!cancelled) setStatus(sub ? saved ? "subscribed" : "registration_unconfirmed" : "subscription_revoked");
      } catch {
        if (!cancelled) setStatus("subscription_revoked");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const requestSubscribe = useCallback(async (): Promise<{ ok: boolean; reason?: string }> => {
    if (!isSupported()) return { ok: false, reason: "unsupported" };
    try {
      // Use the configuration read on mount so permission is requested in
      // the original user gesture, before any network await (Safari/Firefox).
      const publicKey = serverPublicKey.current;
      if (!publicKey) return { ok: false, reason: "Browser push is not configured yet. Refresh this page and try again." };
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus(perm === "denied" ? "denied" : "default");
        return { ok: false, reason: `permission_${perm}` };
      }
      const reg = await readyWorker();
      const sub = await reg.pushManager.getSubscription() ?? await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const res = await fetch("/api/v1/push/subscribe", {
        method: "POST",
        credentials: "include",
        signal: AbortSignal.timeout(8000),
        headers: {
          "content-type": "application/json",
          ...getCsrfHeader(),
        },
        body: JSON.stringify(sub.toJSON()),
      });
      const result = await res.json().catch(() => null);
      if (!res.ok || result?.ok !== true) {
        setStatus("registration_unconfirmed");
        return { ok: false, reason: registrationSaveFailure(res.status) };
      }
      setStatus("subscribed");
      return { ok: true };
    } catch {
      setStatus("registration_unconfirmed");
      return { ok: false, reason: "Browser registration was not confirmed. Refresh this page and try enabling it again." };
    }
  }, []);

  const unsubscribe = useCallback(async (): Promise<{ ok: boolean; reason?: string }> => {
    if (!isSupported()) return { ok: false, reason: "unsupported" };
    try {
      const reg = await readyWorker();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        // Disable server dispatch first. A failed server write must not be
        // reported as successful or discard the browser endpoint needed to retry.
        const response = await fetch("/api/v1/push/subscribe", {
          method: "DELETE", credentials: "include", signal: AbortSignal.timeout(8000),
          headers: {"content-type": "application/json", ...getCsrfHeader()},
          body: JSON.stringify({endpoint: sub.endpoint}),
        });
        if (!response.ok) return {ok: false, reason: "The server could not disable this browser. Try again."};
        if (!await sub.unsubscribe()) return {ok: false, reason: "Server alerts are disabled, but the browser could not remove its registration. Try again."};
      }
      setStatus("default");
      return { ok: true };
    } catch { return {ok: false, reason: "This browser could not be disabled. Try again."}; }
  }, []);

  const requestTest = useCallback(async (workspaceId?: string): Promise<BrowserPushTestResult> => {
    if (!isSupported()) return {ok: false, reason: "unsupported"};
    try {
      const subscription = await (await readyWorker()).pushManager.getSubscription();
      if (!subscription) return {ok: false, reason: "Enable this browser before sending a test."};
      const response = await fetch("/api/v1/push/test", {
        method: "POST", credentials: "include", signal: AbortSignal.timeout(12000),
        headers: {"content-type": "application/json", ...getCsrfHeader()},
        body: JSON.stringify({endpoint: subscription.endpoint, ...(workspaceId ? {workspace_id: workspaceId} : {})}),
      });
      const result = await response.json();
      if (!response.ok) return {ok: false, reason: typeof result.error === "string" ? result.error : "The browser test failed."};
      if (result.registration_disabled) setStatus("subscription_revoked");
      return {ok: result.provider_accepted === true, provider_accepted: result.provider_accepted === true,
        device_display_confirmed: false, in_app_receipt_id: result.in_app_receipt_id ?? null,
        message: result.message, reason: result.provider_accepted === true ? undefined : result.message};
    } catch { return {ok: false, reason: "The test response was not confirmed. Check this browser before retrying."}; }
  }, []);

  // Listen for choreography requests from the AI. The MCP tool
  // `request_push_subscription` broadcasts a CustomEvent on `window`
  // (via the BrowserBus dispatcher's window-event fallback); we catch
  // it here and walk the user through the permission flow.
  useEffect(() => {
    if (!listenForRequests) return;
    function onRequest() {
      void requestSubscribe();
    }
    window.addEventListener("cmcp:request-push-subscription", onRequest);
    return () => window.removeEventListener("cmcp:request-push-subscription", onRequest);
  }, [requestSubscribe, listenForRequests]);

  return { status, serverEnabled, requestSubscribe, unsubscribe, requestTest };
}
